import { memo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Alert } from '@bai/components';
import { handlePolicyManagerClick } from '@bai/features';
import { useLogout, useUser } from '@bai/features/user';
import { addValueToGlobals, basePath } from '@bai/utils';
import { useTheme } from '@bai/utils/theme';
import {
  getDesignTokensEndpoint as buildDesignTokensEndpoint,
  TimesInMilliseconds,
} from '@bai/utils/types';

import UniversalHeaderHelp from './UniversalHeaderHelp';

declare global {
  interface Window {
    universalHeaderScriptsLoaded?: boolean;
    universalHeaderDesignTokensError?: boolean;
    designTokens?: unknown;
    universalHeaderPrimaryDesignTokensFailed?: boolean;
    setupUserDropdown?: () => void;
    init?: () => Promise<void>;
    getDesignTokensEndpoint?: () => string;
    universalHeaderAssetBaseUrl?: string;
  }
}

type UniversalHeaderProps = {
  templateUrl?: string;
  className?: string;
  onLoadError?: (error: unknown) => void;
};

function appendUniversalHeaderScriptsOnce(root: HTMLElement) {
  if (window.universalHeaderScriptsLoaded) {
    return;
  }

  const scriptNodes = Array.from(root.querySelectorAll('script'));

  scriptNodes.forEach((oldScript) => {
    const newScript = document.createElement('script');

    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });

    if (oldScript.src) {
      newScript.src = oldScript.src;
    } else {
      newScript.text = oldScript.textContent ?? '';
    }

    document.body.appendChild(newScript);
  });

  window.universalHeaderScriptsLoaded = true;
}

function UniversalHeader({
  templateUrl = `${basePath}assets/universal-header/header-template.html`,
  className,
  onLoadError,
}: UniversalHeaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoadedTemplateRef = useRef(false);
  const [hasError, setHasError] = useState(false);
  const [plmError, setPlmError] = useState(false);
  const navigate = useNavigate();
  const { branding, hasCustomColor } = useTheme();
  const user = useUser();
  const { logout } = useLogout();

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const logoutClickHandler = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      const logoutLink = target?.closest(
        '[data-cy="unified-header-logout-icon"]',
      ) as HTMLAnchorElement | null;

      if (!logoutLink || !container.contains(logoutLink)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if ('stopImmediatePropagation' in event) {
        event.stopImmediatePropagation();
      }

      void logout();
    };

    container.addEventListener('click', logoutClickHandler, true);

    return () => {
      container.removeEventListener('click', logoutClickHandler, true);
    };
  }, [logout]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const policyManagerClickHandler = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      const pmLink = target?.closest(
        '[data-cy="unified-header-appDrawer_PM-icon"]',
      ) as HTMLAnchorElement | null;

      if (!pmLink || !container.contains(pmLink)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if ('stopImmediatePropagation' in event) {
        event.stopImmediatePropagation();
      }

      void handlePolicyManagerClick(user).catch(() => {
        setPlmError(true);
        setTimeout(() => setPlmError(false), TimesInMilliseconds.THREE_SECONDS);
      });
    };

    container.addEventListener('click', policyManagerClickHandler, true);

    return () => {
      container.removeEventListener('click', policyManagerClickHandler, true);
    };
  }, [user]);

  // Set userId to globals when user is authenticated
  useEffect(() => {
    if (user?.id) {
      addValueToGlobals('userId', user.id);
    }
  }, [user?.id]);

  useEffect(() => {
    window.universalHeaderAssetBaseUrl = new URL(
      './',
      new URL(templateUrl, window.location.href),
    ).toString();

    window.getDesignTokensEndpoint = () => {
      const globals = window.LEGACY_GLOBALS;

      return buildDesignTokensEndpoint(
        globals?.environment,
        globals?.userId,
        globals?.apiBaseUrl,
      );
    };
  }, [templateUrl]);

  useEffect(() => {
    const container = containerRef.current;
    let isMounted = true;
    let logoClickHandler: ((event: Event) => void) | null = null;

    const loadTemplate = async () => {
      try {
        if (!container || hasLoadedTemplateRef.current) {
          return;
        }

        // Ensure userId is available before loading template
        // If user is not loaded yet, wait for it
        if (!user?.id) {
          return;
        }

        const response = await fetch(templateUrl, {
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch universal header template: ${response.status} ${response.statusText}`,
          );
        }

        const html = await response.text();

        if (!isMounted) {
          return;
        }

        const assetBase = new URL(
          './',
          new URL(templateUrl, window.location.href),
        ).href;
        const processedHtml = html.replace(
          /url\(['"]?\.\/assets\//g,
          `url('${assetBase}assets/`,
        );

        container.innerHTML = '';

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = processedHtml;

        Array.from(tempDiv.childNodes).forEach((node) => {
          if (node.nodeName.toLowerCase() !== 'script') {
            container.appendChild(node.cloneNode(true));
          }
        });

        appendUniversalHeaderScriptsOnce(tempDiv);
        hasLoadedTemplateRef.current = true;

        const initializeHeader = async () => {
          if (typeof window.init === 'function') {
            try {
              await window.init();
            } catch {
              // ignore init errors
            }
          }

          // Dispatch design-tokens status event based on whether window.designTokens
          // was populated by the vendor script and whether the primary endpoint
          // failed. Treat primary failures as an error even if the local fallback
          // populated tokens.
          const primaryFailed =
            window.universalHeaderPrimaryDesignTokensFailed === true;
          const hasError = window.designTokens == null || primaryFailed;
          window.universalHeaderDesignTokensError = hasError;

          window.dispatchEvent(
            new CustomEvent(
              hasError
                ? 'universal-header-design-tokens-error'
                : 'universal-header-design-tokens-loaded',
            ),
          );

          if (typeof window.setupUserDropdown === 'function') {
            window.setupUserDropdown();
          }
        };

        await initializeHeader();

        const logoContainer = container.querySelector(
          '[data-cy="unified-header-logo"]',
        );

        if (logoContainer && branding?.logo) {
          const logoLink = '/student/home/';

          logoContainer.innerHTML = `
            <a href="${logoLink}" style="display: flex; align-items: flex-end;">
              <img
                alt="${hasCustomColor ? 'logo' : 'BAI logo'}"
                data-cy="Logo"
                src="${branding.logo}"
                style="margin-left: 21px; width: 10em; max-height: 7em; object-fit: contain; flex-wrap: nowrap; align-self: flex-end;"
              />
            </a>
          `;

          const linkElement = logoContainer.querySelector('a');

          if (linkElement) {
            logoClickHandler = (event: Event) => {
              event.preventDefault();
              navigate(logoLink);
            };

            linkElement.addEventListener('click', logoClickHandler);
          }
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setHasError(true);
        onLoadError?.(error);
      }
    };

    void loadTemplate();

    return () => {
      isMounted = false;

      const logoLink = container?.querySelector(
        '[data-cy="unified-header-logo"] a',
      );

      if (logoLink && logoClickHandler) {
        logoLink.removeEventListener('click', logoClickHandler);
      }
    };
  }, [templateUrl, onLoadError, user?.id]);

  if (hasError) {
    return null;
  }

  return (
    <div className={className}>
      <UniversalHeaderHelp containerRef={containerRef} />
      <div ref={containerRef} data-cy="universal-header-host" />
      {plmError && (
        <Alert severity="error" data-cy="plm-redirect-error">
          Policy Manager is currently unavailable. Please try again in a few
          minutes.
        </Alert>
      )}
    </div>
  );
}

// Memoize component to prevent rerenders from parent route changes
export default memo(UniversalHeader);
